import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ContentStatusBadge } from "~/components/admin/content-status-badge";
import { FaqEditor } from "~/components/admin/faq-editor";
import { ReverseLookupCard } from "~/components/admin/reverse-lookup-card";
import { ChallengeSelection } from "~/components/challenge-selection";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { deleteCoachFrameworkFn } from "~/lib/functions/admin-verifications";
import { cihCcosQueryOptions } from "~/lib/functions/cih/ccos";
import { getChallengesFn } from "~/lib/functions/cih/challenges";
import {
  approveFrameworkFn,
  archiveFrameworkFn,
  createFrameworkDraftFn,
  frameworkQueryKeys,
  frameworkQueryOptions,
  getCoachesByFrameworkFn,
  getFrameworkChallengeIdsFn,
  getFrameworkCtpsByIdFn,
  promoteFrameworkDraftFn,
  publishFrameworkFn,
  restoreFrameworkFn,
  returnFrameworkToDraftFn,
  syncFrameworkChallengesFn,
  updateFrameworkFn,
  type FrameworkRow,
  type FrameworkWithRelations,
  type PaginatedFrameworkCoaches,
} from "~/lib/functions/cih/frameworks";
import {
  FRAMEWORK_TYPE_OPTIONS,
  type FrameworkType,
} from "~/lib/shared/content-status";

export const Route = createFileRoute("/admin/cih/frameworks/$frameworkId")({
  staticData: { title: "Edit Framework" },
  loader: async ({ context, params }) => {
    const [
      data,
      ,
      linkedChallengeIds,
      allChallenges,
      frameworkCtps,
      coachData,
    ] = await Promise.all([
      context.queryClient.ensureQueryData(
        frameworkQueryOptions(params.frameworkId),
      ),
      context.queryClient.ensureQueryData(cihCcosQueryOptions()),
      getFrameworkChallengeIdsFn({
        data: { frameworkId: params.frameworkId },
      }),
      getChallengesFn(),
      getFrameworkCtpsByIdFn({
        data: { frameworkId: params.frameworkId },
      }),
      getCoachesByFrameworkFn({
        data: { frameworkId: params.frameworkId, page: 1, limit: 50 },
      }),
    ]);
    return {
      title: data?.name ?? "Edit Framework",
      linkedChallengeIds,
      allChallenges: allChallenges.map((c) => ({ id: c.id, title: c.title })),
      frameworkCtps,
      coachData,
    };
  },
  component: FrameworkEditorPage,
});

function FrameworkEditorPage() {
  const { frameworkId } = Route.useParams();
  const { linkedChallengeIds, allChallenges, frameworkCtps, coachData } =
    Route.useLoaderData();
  const { data: framework, isLoading } = useQuery(
    frameworkQueryOptions(frameworkId),
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!framework) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Framework not found.</div>
      </div>
    );
  }

  return (
    <FrameworkEditorForm
      frameworkId={frameworkId}
      framework={framework}
      initialChallengeIds={linkedChallengeIds}
      allChallenges={allChallenges}
      frameworkCtps={frameworkCtps}
      initialCoachData={coachData}
    />
  );
}

function FrameworkEditorForm({
  frameworkId,
  framework,
  initialChallengeIds,
  allChallenges,
  frameworkCtps,
  initialCoachData,
}: {
  frameworkId: string;
  framework: FrameworkRow;
  initialChallengeIds: string[];
  allChallenges: { id: string; title: string }[];
  frameworkCtps: FrameworkWithRelations["ctps"];
  initialCoachData: PaginatedFrameworkCoaches;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedChallengeIds, setSelectedChallengeIds] =
    useState<string[]>(initialChallengeIds);
  const [coachData, setCoachData] = useState(initialCoachData);

  const { data: ccos } = useQuery(cihCcosQueryOptions());

  const form = useForm({
    defaultValues: {
      name: framework.name,
      type: (framework.type as FrameworkType) ?? "framework",
      description: framework.description ?? "",
      ccoId: framework.ccoId ?? "",
      body: framework.body != null ? JSON.stringify(framework.body) : "",
      pullQuote: framework.pullQuote,
      pullQuoteAttribution: framework.pullQuoteAttribution,
      narrativeStats: framework.narrativeStats,
      hidePlatformStats: framework.hidePlatformStats,
      faqs: (framework.faqs ?? []) as { question: string; answer: string }[],
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: frameworkQueryKeys.all });
    queryClient.invalidateQueries({
      queryKey: frameworkQueryKeys.detail(frameworkId),
    });
  };

  const getFormData = () => {
    const v = form.state.values;
    return {
      id: frameworkId,
      name: v.name,
      type: v.type,
      description: v.description,
      ccoId: v.ccoId,
      body: v.body,
      pullQuote: v.pullQuote,
      pullQuoteAttribution: v.pullQuoteAttribution,
      narrativeStats: v.narrativeStats,
      hidePlatformStats: v.hidePlatformStats,
      faqs: v.faqs,
    };
  };

  const saveAll = async () => {
    await updateFrameworkFn({ data: getFormData() });
    await syncFrameworkChallengesFn({
      data: { frameworkId, challengeIds: selectedChallengeIds },
    });
  };

  const updateMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      invalidate();
      showSuccess("Framework saved.");
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { data: { id: string } }) => {
      await saveAll();
      return publishFrameworkFn(data);
    },
    onSuccess: () => {
      invalidate();
      showSuccess("Framework published.");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: archiveFrameworkFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Framework archived.");
    },
  });

  const createDraftMutation = useMutation({
    mutationFn: createFrameworkDraftFn,
    onSuccess: (newDraft) => {
      invalidate();
      navigate({
        to: "/admin/cih/frameworks/$frameworkId",
        params: { frameworkId: newDraft.id },
      });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (data: { data: { draftId: string } }) => {
      await saveAll();
      return promoteFrameworkDraftFn(data);
    },
    onSuccess: (promoted) => {
      queryClient.removeQueries({
        queryKey: frameworkQueryKeys.detail(frameworkId),
      });
      invalidate();
      navigate({
        to: "/admin/cih/frameworks/$frameworkId",
        params: { frameworkId: promoted.id },
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: approveFrameworkFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Framework approved and published.");
    },
  });

  const returnToDraftMutation = useMutation({
    mutationFn: returnFrameworkToDraftFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Framework returned to draft.");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: restoreFrameworkFn,
    onSuccess: () => {
      invalidate();
      showSuccess("Framework restored to draft.");
    },
  });

  const handleSave = (): void => {
    updateMutation.mutate();
  };

  const handleReturnToDraft = (): void => {
    const note = window.prompt("Feedback note for the author:");
    if (!note) return;
    returnToDraftMutation.mutate({ data: { id: frameworkId, note } });
  };

  const removeCoachMutation = useMutation({
    mutationFn: deleteCoachFrameworkFn,
    onSuccess: async () => {
      const updated = await getCoachesByFrameworkFn({
        data: { frameworkId, page: 1, limit: 50 },
      });
      setCoachData(updated);
    },
  });

  const anyPending =
    updateMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending ||
    createDraftMutation.isPending ||
    promoteMutation.isPending ||
    approveMutation.isPending ||
    returnToDraftMutation.isPending ||
    restoreMutation.isPending;

  const mutationError =
    updateMutation.error ??
    publishMutation.error ??
    archiveMutation.error ??
    createDraftMutation.error ??
    promoteMutation.error ??
    approveMutation.error ??
    returnToDraftMutation.error ??
    restoreMutation.error;

  const isDraft = framework.status === "draft";
  const isPublished = framework.status === "published";
  const isInReview = framework.status === "in_review";
  const isArchived = framework.status === "archived";
  const hasDraftParent = framework.draftOf != null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-medium text-gray-900">
                {framework.name}
              </h1>
              <ContentStatusBadge status={framework.status} />
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <span>Framework editor — ID: {frameworkId}</span>
              {framework.slug && framework.status === "published" && (
                <a
                  href={`/frameworks/${framework.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  View public page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {isDraft && !hasDraftParent && (
              <>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={anyPending}
                  loading={updateMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    publishMutation.mutate({ data: { id: frameworkId } })
                  }
                  disabled={anyPending}
                  loading={publishMutation.isPending}
                >
                  Publish
                </Button>
              </>
            )}
            {isDraft && hasDraftParent && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={anyPending}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            )}
            {isPublished && (
              <>
                <Button
                  variant="default"
                  onClick={() =>
                    createDraftMutation.mutate({
                      data: { publishedId: frameworkId },
                    })
                  }
                  disabled={anyPending}
                  loading={createDraftMutation.isPending}
                >
                  Create Draft
                </Button>
                <Button
                  variant="destructive"
                  onClick={() =>
                    archiveMutation.mutate({ data: { id: frameworkId } })
                  }
                  disabled={anyPending}
                  loading={archiveMutation.isPending}
                >
                  Archive
                </Button>
              </>
            )}
            {isInReview && (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    approveMutation.mutate({ data: { id: frameworkId } })
                  }
                  disabled={anyPending}
                  loading={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="default"
                  onClick={handleReturnToDraft}
                  disabled={anyPending}
                  loading={returnToDraftMutation.isPending}
                >
                  Return to Draft
                </Button>
              </>
            )}
            {isArchived && (
              <Button
                variant="default"
                onClick={() =>
                  restoreMutation.mutate({ data: { id: frameworkId } })
                }
                disabled={anyPending}
                loading={restoreMutation.isPending}
              >
                Restore to Draft
              </Button>
            )}
          </div>
        </div>

        {/* Draft-of banner */}
        {hasDraftParent && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-700">
              Editing draft of published content. Save your changes, then
              promote when ready.
            </p>
            <Button
              size="sm"
              onClick={() =>
                promoteMutation.mutate({ data: { draftId: frameworkId } })
              }
              disabled={anyPending}
              loading={promoteMutation.isPending}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Promote to Live
            </Button>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {mutationError instanceof Error
              ? mutationError.message
              : "An error occurred. Please try again."}
          </div>
        )}

        {/* Form */}
        <Card className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="name">
              {(field) => (
                <div>
                  <Label htmlFor="fw-name" className="mb-1">
                    Name
                  </Label>
                  <Input
                    id="fw-name"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
            <div>
              <Label htmlFor="fw-slug" className="mb-1">
                Slug
              </Label>
              <Input
                id="fw-slug"
                type="text"
                value={framework.slug ?? ""}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <form.Field name="type">
              {(field) => (
                <div>
                  <Label htmlFor="fw-type" className="mb-1">
                    Type
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(val) =>
                      field.handleChange(val as FrameworkType)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FRAMEWORK_TYPE_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="ccoId">
              {(field) => (
                <div>
                  <Label htmlFor="fw-cco" className="mb-1">
                    Associated CCO{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <Select
                    value={field.state.value || "__none__"}
                    onValueChange={(val) =>
                      field.handleChange(val === "__none__" ? "" : val)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {ccos?.map((cco) => (
                        <SelectItem key={cco.id} value={cco.id}>
                          {cco.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="description">
            {(field) => (
              <div>
                <Label htmlFor="fw-description" className="mb-1">
                  Description
                </Label>
                <Textarea
                  id="fw-description"
                  rows={3}
                  value={field.state.value}
                  onValueChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="body">
            {(field) => (
              <div>
                <Label className="mb-1">Body</Label>
                <RichTextEditor
                  content={field.state.value}
                  onChange={field.handleChange}
                />
              </div>
            )}
          </form.Field>
        </Card>

        {/* Narrative (dark band) */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Narrative & Stats
          </h2>
          <form.Field name="pullQuote">
            {(field) => (
              <div>
                <Label htmlFor="fw-pull-quote" className="mb-1">
                  Pull quote (rendered in the dark § 02 band)
                </Label>
                <Textarea
                  id="fw-pull-quote"
                  rows={3}
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="One sentence that captures the framework's essence"
                />
              </div>
            )}
          </form.Field>

          <form.Field name="pullQuoteAttribution">
            {(field) => (
              <div className="mt-4">
                <Label htmlFor="fw-pull-quote-attr" className="mb-1">
                  Pull quote attribution
                </Label>
                <Input
                  id="fw-pull-quote-attr"
                  value={field.state.value ?? ""}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  placeholder="e.g., Framework Author"
                />
              </div>
            )}
          </form.Field>

          <form.Field name="narrativeStats" mode="array">
            {(field) => (
              <div className="mt-4">
                <Label className="mb-1">Narrative stats (up to 4)</Label>
                <p className="mb-2 text-xs text-gray-500">
                  Big numbers about the framework. Render in the hero stat strip
                  before platform-derived stats.
                </p>
                {(field.state.value ?? []).map((_, i) => (
                  <div key={i} className="mb-2 flex gap-2">
                    <form.Field name={`narrativeStats[${i}].value`}>
                      {(f) => (
                        <Input
                          placeholder="40+"
                          value={f.state.value ?? ""}
                          onValueChange={f.handleChange}
                          className="w-32"
                        />
                      )}
                    </form.Field>
                    <form.Field name={`narrativeStats[${i}].label`}>
                      {(f) => (
                        <Input
                          placeholder="Years in practice"
                          value={f.state.value ?? ""}
                          onValueChange={f.handleChange}
                          className="flex-1"
                        />
                      )}
                    </form.Field>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => field.removeValue(i)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                {(field.state.value?.length ?? 0) < 4 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => field.pushValue({ value: "", label: "" })}
                  >
                    + Add stat
                  </Button>
                ) : null}
              </div>
            )}
          </form.Field>

          <form.Field name="hidePlatformStats">
            {(field) => (
              <div className="mt-4 flex items-center gap-2">
                <input
                  id="fw-hide-platform-stats"
                  type="checkbox"
                  checked={!!field.state.value}
                  onChange={(e) => field.handleChange(e.target.checked)}
                />
                <Label htmlFor="fw-hide-platform-stats">
                  Hide platform-derived stats (only show narrative stats above)
                </Label>
              </div>
            )}
          </form.Field>
        </Card>

        {/* Challenges */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Challenges
          </h2>
          <p className="mb-4 text-sm text-gray-400">
            Link challenges that this framework addresses.
          </p>
          <ChallengeSelection
            selectedIds={selectedChallengeIds}
            onSelectionChange={setSelectedChallengeIds}
            challenges={allChallenges}
          />
        </Card>

        {/* FAQs */}
        <Card className="mt-6">
          <h2 className="mb-4 text-sm font-medium tracking-wide text-gray-500 uppercase">
            FAQs
          </h2>
          <form.Field name="faqs">
            {(field) => (
              <FaqEditor
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </Card>

        {/* Training Programs teaching this framework — reverse-lookup */}
        <ReverseLookupCard
          title="Training Programs Teaching This Framework"
          summary={`${frameworkCtps.length} training program${frameworkCtps.length === 1 ? "" : "s"} teach this framework.`}
          items={frameworkCtps.map((ctp) => ({
            id: ctp.id,
            label: ctp.name,
            href: `/admin/cih/ctps/${ctp.id}`,
          }))}
          pointer="To add or remove CTPs here, open that CTP's editor and edit its Frameworks Taught list."
          className="mt-6"
        />

        {/* Coaches claiming this framework — reverse-lookup */}
        <Card className="mt-6">
          <h2 className="mb-3 text-sm font-medium tracking-wide text-gray-500 uppercase">
            Coaches Claiming This Framework
          </h2>
          <p className="mb-3 text-sm text-gray-700">
            {coachData.total} coach{coachData.total === 1 ? "" : "es"} claim
            this framework.
            {coachData.total > coachData.coaches.length && (
              <span className="text-gray-400">
                {" "}
                Showing first {coachData.coaches.length}.
              </span>
            )}
          </p>

          {coachData.coaches.length > 0 && (
            <ul className="mb-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
              {coachData.coaches.map((coach) => (
                <li
                  key={coach.id}
                  className="flex items-center justify-between px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <a
                      href={`/${coach.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-600 hover:underline"
                    >
                      {coach.fullName}
                    </a>
                    {coach.credential && (
                      <span className="text-xs text-gray-400">
                        {coach.credential}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Remove ${coach.fullName}'s claim on this framework? This cannot be undone.`,
                        )
                      )
                        return;
                      removeCoachMutation.mutate({
                        data: {
                          coachId: coach.id,
                          frameworkId,
                        },
                      });
                    }}
                    disabled={removeCoachMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-1 text-xs text-gray-400 italic">
            Coaches add themselves here from their profile; use Remove only to
            clean up bad claims.
          </p>
        </Card>

        {/* Sticky save for draft */}
        {isDraft && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={anyPending}
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
